import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { MapPin, Route as RouteIcon, Map, Mountain, Bike, Award, Zap, Footprints, Car, Upload, Trash2 } from 'lucide-react';
import { useState, useEffect } from 'react';
import WeatherWidget from '@/components/WeatherWidget';
import RangeCalculator from '@/components/RangeCalculator';
import './HomePage.css';
import heroScotland from './hero-scotland.jpg';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function HomePage() {
  const { t } = useTranslation();
  const [galleryImages, setGalleryImages] = useState([]);
  const [displayLimit, setDisplayLimit] = useState(12); // 2 Reihen = 12 Bilder (6 pro Reihe)
  const [sortOrder, setSortOrder] = useState('newest'); // Feature 4: Sort
  const [deleteMode, setDeleteMode] = useState(false); // Delete mode toggle

  const features = [
    {
      icon: <RouteIcon size={32} />,
      title: t('home.feature1_title'),
      desc: t('home.feature1_desc')
    },
    {
      icon: <Zap size={32} />,
      title: t('home.feature2_title'),
      desc: t('home.feature2_desc')
    },
    {
      icon: <MapPin size={32} />,
      title: t('home.feature3_title'),
      desc: t('home.feature3_desc')
    },
    {
      icon: <Award size={32} />,
      title: t('home.feature4_title'),
      desc: t('home.feature4_desc')
    }
  ];

  const regions = [
    { name: 'Highlands', image: '🏔️', desc: 'region_highlands_desc' },
    { name: 'Islands', image: '🏝️', desc: 'region_islands_desc' },
    { name: 'Cities', image: '🏰', desc: 'region_cities_desc' },
    { name: 'Borders', image: '🌳', desc: 'region_borders_desc' }
  ];

  // Load gallery images from backend
  useEffect(() => {
    const loadGalleryImages = async () => {
      try {
        // Add cache buster to force fresh data
        const cacheBuster = Date.now();
        const response = await fetch(`${BACKEND_URL}/api/gallery-images?_=${cacheBuster}`, {
          cache: 'no-store'
        });
        if (response.ok) {
          const data = await response.json();
          let images = data.images || [];
          
          // Feature 4: Sort images based on selection
          if (sortOrder === 'newest') {
            images = images.reverse(); // Newest first (EXIF date)
          } else if (sortOrder === 'oldest') {
            // Already sorted by oldest (EXIF date) from backend
          }
          
          setGalleryImages(images);
        }
      } catch (error) {
        console.error('Error loading gallery images:', error);
      }
    };
    loadGalleryImages();
  }, [sortOrder]);

  // Delete image function
  const handleDeleteImage = async (filename) => {
    if (!window.confirm('Möchten Sie dieses Bild wirklich löschen?')) {
      return;
    }

    try {
      const encodedFilename = encodeURIComponent(filename);
      const response = await fetch(`${BACKEND_URL}/api/gallery-images/${encodedFilename}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        // Remove image from state
        setGalleryImages(prev => prev.filter(img => img.filename !== filename));
        alert('Bild erfolgreich gelöscht!');
      } else {
        alert('Fehler beim Löschen des Bildes.');
      }
    } catch (error) {
      console.error('Error deleting image:', error);
      alert('Fehler beim Löschen des Bildes.');
    }
  };

  return (
    <div className="home-page" data-testid="home-page">
      {/* Hero Section */}
      <section className="hero" style={{ backgroundImage: `url(${heroScotland})` }} data-testid="hero-section">
        <div className="hero-content">
          <div className="hero-logo-large">
            <img src="/logo.svg" alt="PedalScot Logo" />
          </div>
          <h1 className="hero-title" data-testid="hero-title">
            {t('home.hero_title')}
          </h1>
          <p className="hero-subtitle" data-testid="hero-subtitle">
            {t('home.hero_subtitle')}
          </p>
          <div className="hero-buttons">
            <Link to="/routes" className="btn btn-hero-primary" data-testid="discover-routes-btn">
              <RouteIcon size={20} />
              {t('home.discover_routes')}
            </Link>
            <Link to="/map" className="btn btn-hero-secondary" data-testid="open-map-btn">
              <Map size={20} />
              {t('home.open_map')}
            </Link>
          </div>
          <div className="hero-stats">
            <div className="hero-stat">
              <div className="stat-number">600+</div>
              <div className="stat-label">{t('home.stats_routes')}</div>
            </div>
            <div className="hero-stat">
              <div className="stat-number">100+</div>
              <div className="stat-label">{t('home.stats_pois')}</div>
            </div>
            <div className="hero-stat">
              <div className="stat-number">1000+</div>
              <div className="stat-label">{t('home.stats_users')}</div>
            </div>
          </div>
        </div>
        <div className="hero-visual">
          <div className="hero-image-wrapper">
            <img 
              src="https://images.unsplash.com/photo-1698250162577-e0d70eba2698" 
              alt="Cycling in Scottish Highlands"
              className="hero-image"
            />
          </div>
        </div>
      </section>

      {/* Founder Story Section */}
      <section className="founder-story" data-testid="founder-story-section">
        <div className="story-container">
          <div className="story-badge">
            <Footprints size={18} />
            <Bike size={18} />
            Von einem Wanderer und Radfahrer für euch
          </div>
          <h2 className="story-title">{t('home.story_title')}</h2>
          <div className="story-content">
            <p className="story-highlight">
              {t('home.story_intro')}
            </p>
            <p>
              {t('home.story_problem')}
            </p>
            <p>
              {t('home.story_solution')}
            </p>
            <div className="story-list">
              <h3>{t('home.story_includes_title')}</h3>
              <ul>
                <li>✅ {t('home.story_item1')}</li>
                <li>✅ {t('home.story_item2')}</li>
                <li>✅ {t('home.story_item3')}</li>
                <li>✅ {t('home.story_item4')}</li>
              </ul>
            </div>
            <p className="story-closing">
              {t('home.story_closing')}
            </p>
          </div>
        </div>
      </section>

      {/* Activities Section */}
      <section className="activities" data-testid="activities-section">
        <h2 className="section-title">{t('home.activities_title')}</h2>
        <p className="section-subtitle">{t('home.activities_subtitle')}</p>
        <div className="activities-grid">
          <Link to="/routes?activity_type=cycling" className="activity-card activity-cycling" data-testid="activity-cycling">
            <div className="activity-icon">
              <Bike size={48} />
            </div>
            <h3>{t('home.activity_cycling_title')}</h3>
            <p>{t('home.activity_cycling_desc')}</p>
            <div className="activity-cta">{t('home.explore_routes')} →</div>
          </Link>
          
          <Link to="/routes?activity_type=hiking" className="activity-card activity-hiking" data-testid="activity-hiking">
            <div className="activity-icon">
              <Footprints size={48} />
            </div>
            <h3>{t('home.activity_hiking_title')}</h3>
            <p>{t('home.activity_hiking_desc')}</p>
            <div className="activity-cta">{t('home.explore_routes')} →</div>
          </Link>
          
          <Link to="/routes?activity_type=roadtrip" className="activity-card activity-roadtrip" data-testid="activity-roadtrip">
            <div className="activity-icon">
              <Car size={48} />
            </div>
            <h3>{t('home.activity_roadtrip_title')}</h3>
            <p>{t('home.activity_roadtrip_desc')}</p>
            <div className="activity-cta">{t('home.explore_routes')} →</div>
          </Link>
        </div>
      </section>

      {/* Features Section */}
      <section className="features" data-testid="features-section">
        <h2 className="section-title">{t('home.features_title')}</h2>
        <div className="features-grid">
          {features.map((feature, idx) => (
            <div key={idx} className="feature-card" data-testid={`feature-card-${idx}`}>
              <div className="feature-icon">{feature.icon}</div>
              <h3 className="feature-title">{feature.title}</h3>
              <p className="feature-desc">{feature.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Regions Section */}
      <section className="regions" data-testid="regions-section">
        <h2 className="section-title">{t('home.regions_title')}</h2>
        <div className="regions-grid">
          {regions.map((region, idx) => (
            <Link 
              key={idx} 
              to={`/routes?region=${region.name}`} 
              className="region-card"
              data-testid={`region-card-${region.name}`}
            >
              <div className="region-image">{region.image}</div>
              <h3 className="region-name">{t(`regions.${region.name}`)}</h3>
              <p className="region-desc">{t(`home.${region.desc}`)}</p>
            </Link>
          ))}
        </div>
      </section>

      {/* E-Bike Tools Section */}
      <section className="ebike-tools" data-testid="ebike-tools-section">
        <h2 className="section-title">{t('home.ebike_tools_title')}</h2>
        <div className="tools-grid">
          <div className="tool-item">
            <WeatherWidget latitude={56.5} longitude={-4.2} />
          </div>
          <div className="tool-item">
            <Link to="/charging" className="charging-preview">
              <Zap size={48} />
              <h3>{t('home.charging_stations_title')}</h3>
              <p>{t('home.charging_stations_desc')}</p>
              <button className="btn btn-secondary">{t('home.show_charging_stations')}</button>
            </Link>
          </div>
        </div>
      </section>

      {/* Range Calculator Section */}
      <section className="range-section" data-testid="range-section">
        <RangeCalculator />
      </section>

      {/* Scotland Gallery Section */}
      <section className="scotland-gallery" data-testid="gallery-section">
        <div className="gallery-header">
          <h2>Schottland durch meine Linse</h2>
          <p>Eindrücke meiner Touren durch die Highlands ({galleryImages.length} Fotos)</p>
          
          {/* Feature 4: Sort controls */}
          <div className="gallery-controls">
            <select 
              value={sortOrder} 
              onChange={(e) => setSortOrder(e.target.value)}
              className="sort-select"
            >
              <option value="newest">Neueste zuerst (Aufnahmedatum)</option>
              <option value="oldest">Älteste zuerst (Aufnahmedatum)</option>
            </select>
            
            <button 
              onClick={() => setDeleteMode(!deleteMode)}
              className={`btn-delete-mode ${deleteMode ? 'active' : ''}`}
            >
              <Trash2 size={20} />
              {deleteMode ? 'Fertig' : 'Bilder löschen'}
            </button>
            
            <Link to="/upload-gallery" className="btn-upload-gallery">
              <Upload size={20} />
              Neue Bilder hochladen
            </Link>
          </div>
        </div>
        
        {/* Feature 5: Optimized Grid */}
        <div className="gallery-grid-optimized">
          {galleryImages.length > 0 ? (
            galleryImages.slice(0, displayLimit).map((image, index) => (
              <div className="gallery-item-optimized" key={index}>
                <img 
                  src={image.url} 
                  alt={`Scotland ${index + 1}`} 
                  loading="lazy"
                />
                {deleteMode && (
                  <button 
                    className="delete-image-btn"
                    onClick={() => handleDeleteImage(image.filename)}
                    title="Bild löschen"
                  >
                    <Trash2 size={20} />
                  </button>
                )}
              </div>
            ))
          ) : (
            <div className="gallery-empty">
              <p>Keine Bilder vorhanden. Laden Sie Ihre ersten Bilder hoch!</p>
              <Link to="/upload-gallery" className="btn btn-primary">
                <Upload size={20} />
                Bilder hochladen
              </Link>
            </div>
          )}
        </div>
        
        {/* Feature 2: Load More Button (Pagination) */}
        {galleryImages.length > displayLimit && (
          <div className="load-more-section">
            <button 
              className="btn-load-more"
              onClick={() => setDisplayLimit(prev => prev + 12)}
            >
              Mehr laden ({galleryImages.length - displayLimit} weitere Bilder)
            </button>
          </div>
        )}
        
        {displayLimit >= galleryImages.length && galleryImages.length > 12 && (
          <div className="load-more-section">
            <button 
              className="btn-load-more"
              onClick={() => setDisplayLimit(12)}
            >
              Weniger anzeigen
            </button>
          </div>
        )}
      </section>

      {/* CTA Section */}
      <section className="cta" data-testid="cta-section">
        <div className="cta-content">
          <h2 className="cta-title">{t('home.cta_title')}</h2>
          <p className="cta-text">{t('home.cta_subtitle')}</p>
          <Link to="/register" className="btn btn-cta" data-testid="start-exploring-btn">
            {t('home.cta_button')}
          </Link>
        </div>
      </section>

      {/* Scotland Guide Section */}
      <section className="scotland-guide" data-testid="guide-section">
        <h2 className="section-title">{t('home.guide_title')}</h2>
        <p className="section-subtitle">{t('home.guide_subtitle')}</p>
        <div className="guide-grid">
          <Link to="/wissen" className="guide-item">📋 {t('home.guide_overview')}</Link>
          <Link to="/wissen/verkehrsregeln" className="guide-item">⚖️ {t('home.guide_rules')}</Link>
          <Link to="/wissen/sicherheit" className="guide-item">🏥 {t('home.guide_safety')}</Link>
          <Link to="/wissen" className="guide-item">🌦️ {t('home.guide_weather')}</Link>
          <Link to="/wissen" className="guide-item">🚂 {t('home.guide_transport')}</Link>
          <Link to="/wissen/ebike" className="guide-item">🔋 {t('home.guide_charging')}</Link>
          <Link to="/wissen" className="guide-item">⛺ {t('home.guide_camping')}</Link>
          <Link to="/routes" className="guide-item">🗺️ {t('home.guide_routes')}</Link>
          <Link to="/wissen/ausruestung" className="guide-item">🎒 {t('home.guide_equipment')}</Link>
          <Link to="/wissen" className="guide-item">🏨 {t('home.guide_accommodation')}</Link>
        </div>
        <p className="guide-tip">{t('home.guide_tip')}</p>
      </section>
    </div>
  );
}
